import { create } from 'zustand';

/**
 * Community drawer state (SmartFi Desktop: App.tsx chatOpen / chatUnread).
 * Lives outside the routes so it survives moving between dashboard pages;
 * open/closed is also remembered across a refresh (desktop).
 */
type View = 'chat' | 'suggestions';
interface DrawerState {
   open: boolean;
   /** What Community shows: the chat, or Suggestions (/suggest). */
   view: View;
   /** Messages that arrived while the drawer was closed. */
   unread: number;
   /** A dashboard page has been shown — Community may connect from now on. */
   shell: boolean;
   /** The phone page (/wifi-community) is showing: the drawer steps aside. */
   page: boolean;
   /** The drawer's Community is mounted (and holds the connection). */
   drawerLive: boolean;
   /** The open channel / conversation — remembered across a reload (e.g. "Switch to this account"). */
   room: string | null;
   setRoom: (room: string) => void;
   setOpen: (open: boolean, view?: View) => void;
   /** Another page asked to start a 1-on-1 with this person (e.g. Partners → Message); Community picks it up. */
   dmWith: string | null;
   messageUser: (userId: string) => void;
   clearDm: () => void;
   toggle: () => void;
   setView: (view: View) => void;
   setUnread: (n: number) => void;
   setShell: (on: boolean) => void;
   setPage: (on: boolean) => void;
   setDrawerLive: (on: boolean) => void;
}

export const DRAWER_KEY = 'smartfi.community.drawer';
export const readSavedOpen = () => {
   try {
      return window.localStorage.getItem(DRAWER_KEY) === 'open';
   } catch {
      return false;
   }
};
const saveOpen = (open: boolean) => {
   try {
      window.localStorage.setItem(DRAWER_KEY, open ? 'open' : 'closed');
   } catch {
      /* not remembered — fine */
   }
};

export const ROOM_KEY = 'smartfi.community.room';
export const readSavedRoom = () => {
   try {
      return window.localStorage.getItem(ROOM_KEY);
   } catch {
      return null;
   }
};

export const useCommunityDrawer = create<DrawerState>((set, get) => ({
   open: readSavedOpen(),
   room: readSavedRoom(),
   setRoom: (room) => {
      try {
         window.localStorage.setItem(ROOM_KEY, room);
      } catch {
         /* not remembered */
      }
      set({ room });
   },
   view: 'chat',
   unread: 0,
   shell: false,
   page: false,
   drawerLive: false,
   setOpen: (open, view) => {
      saveOpen(open);
      set((s) => ({ open, view: view ?? (open ? s.view : 'chat') }));
   },
   toggle: () => get().setOpen(!get().open),
   dmWith: null,
   messageUser: (userId) => {
      get().setOpen(true, 'chat');
      set({ dmWith: userId });
   },
   clearDm: () => set({ dmWith: null }),
   setView: (view) => set({ view }),
   setUnread: (unread) => set({ unread }),
   setShell: (shell) => set({ shell }),
   setPage: (page) => set({ page }),
   setDrawerLive: (drawerLive) => set({ drawerLive }),
}));
